// src/App.js
import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider } from '@material-tailwind/react';
import { AuthProvider, useAuth } from './contexts/AuthContext';

// Layout
import Layout from './components/layout/Layout';

// Pages (all in pages folder)
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Profile from './pages/Profile';
import Account from './pages/Account'; 
import Products from './pages/Products';
import Customers from './pages/Customers';
import Enquiries from './pages/Enquiries';
import Projects from './pages/Projects';
import Payments from './pages/Payments';
import Invoices from './pages/Invoices';
import InvoiceCreator from './pages/InvoiceCreator';
import InvoiceDetailPage from './pages/InvoiceDetailPage'; 
import CommissionCalculator from './pages/CommissionCalculator';
import DocumentsPage from './pages/DocumentsPage';
import NotFound from './pages/NotFound';
import ProjectDetailPage from './pages/ProjectDetailPage';
import FAQ from './pages/FAQ';
import FeedbackPage from './pages/FeedbackPage';
import SocialMediaContentPage from './pages/SocialMediaContent';
import ShareBoutee from './pages/ShareBoutee';
import ReferJeweller from './pages/ReferJeweller';
import ContactSupport from './pages/ContactSupport';
import CommunityHub from './pages/CommunityHub';
import CommunityGroupPage from './pages/CommunityGroupPage';
import CommunityJewellersPage from './pages/CommunityJewellersPage';

// Component-based pages (keeping as-is for now)
import PayoutsPage from './components/payouts/PayoutsPage';

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { currentUser, loading } = useAuth();

  if (loading) {
    return <div className="flex justify-center items-center min-h-screen">Loading...</div>;
  }

  if (!currentUser) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [pathname]);

  return null;
};

const App = () => {
  return (
    <ThemeProvider>
      <AuthProvider>
        <Router>
          <ScrollToTop />
          <Routes>
            <Route path="/login" element={<Login />} />

            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <Layout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Dashboard />} />
              <Route path="profile" element={<Profile />} />
              <Route path="account" element={<Account />} />
              <Route path="products" element={<Products />} />
              <Route path="customers" element={<Customers />} />
              <Route path="enquiries" element={<Enquiries />} />
              <Route path="enquiries/:projectId" element={<ProjectDetailPage />} />
              <Route path="projects" element={<Projects />} />
              <Route path="projects/:projectId" element={<ProjectDetailPage />} /> 
              <Route path="invoices" element={<Invoices />} />
              <Route path="invoices/new" element={<InvoiceCreator />} />
              <Route path="invoices/:invoiceId" element={<InvoiceDetailPage />} />
              <Route path="payouts" element={<PayoutsPage />} />
              <Route path="calculator" element={<CommissionCalculator />} />
              <Route path="documents" element={<DocumentsPage />} />
              <Route path="payments" element={<Payments />} />
              <Route path="community" element={<CommunityHub />} />
              <Route path="community/all-jewellers" element={<CommunityJewellersPage />} />
              <Route path="community/:groupSlug" element={<CommunityGroupPage />} />
              <Route path="*" element={<NotFound />} />
              <Route path="/FAQ" element={<FAQ />} />
              <Route path="feedback" element={<FeedbackPage />} />
              <Route path="social-media" element={<SocialMediaContentPage />} />
              <Route path="/share-boutee" element={<ShareBoutee />} />
              <Route path="/refer-jeweller" element={<ReferJeweller />} />
              <Route path="/contact-support" element={<ContactSupport />} />
            </Route>
          </Routes>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
};

export default App;
